/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import Avatar from "../../components/Avatar";
import { useLanguage } from "../../contexts/LanguageContext";
import { useStore } from "../../contexts/StoreContext";
import { formatNumber, formatRelative } from "../dashboard/format";
import { Skeleton } from "../dashboard/components/ui";
import {
  balanceUnit,
  formatBalance,
  PER_PAGE,
  formatDate,
  pageWindow,
  segmentForLastVisit,
} from "./customerFormat";
import { Pill, SegmentDot } from "./ui";

function pagerClass(enabled) {
  return `min-h-10 rounded-lg border px-3.5 text-[13px] font-medium ${
    enabled
      ? "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
      : "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
  }`;
}

export function CardCell({ pass }) {
  const { t } = useLanguage();
  if (!pass) return <span className="text-gray-400">—</span>;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Pill>{pass.name}</Pill>
      {pass.tier && (
        <span className="text-xs font-medium whitespace-nowrap text-gray-600">
          {t("customers.list.tierBadge", { rank: pass.tier.rank, total: pass.tier.count })}
        </span>
      )}
    </span>
  );
}

function SkeletonRows() {
  return Array.from({ length: 6 }, (_, i) => (
    <tr key={i} className="border-t border-gray-100">
      <td className="px-4 py-3" colSpan={5}>
        <Skeleton className="h-8 w-full" />
      </td>
    </tr>
  ));
}

export default function CustomerTable({
  customers,
  pagination,
  loading,
  stale,
  filtered,
  showSegments,
  selectedId,
  onSelect,
  onPage,
  empty,
}) {
  const { t, language } = useLanguage();
  const { selectedStore } = useStore();
  const now = new Date();
  const current = pagination?.current_page || 1;
  const totalPages = Math.max(1, pagination?.total_pages || 1);
  const total = pagination?.total_count || 0;
  const from = total ? (current - 1) * PER_PAGE + 1 : 0;
  const to = Math.min(current * PER_PAGE, total);
  const summaryValues = {
    from: formatNumber(from, language),
    to: formatNumber(to, language),
    total: formatNumber(total, language),
    count: total,
  };

  return (
    <section
      aria-label={t("customers.list.label")}
      aria-busy={loading || stale ? "true" : "false"}
      className="min-w-0 flex-[999_1_560px] overflow-hidden rounded-[14px] border border-gray-200 bg-white"
    >
      <div className={`relative overflow-x-auto transition-opacity ${stale ? "opacity-60" : ""}`}>
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th scope="col" className="px-4 py-3 text-xs font-semibold text-gray-600">
                {t("customers.table.customer")}
              </th>
              <th scope="col" className="px-4 py-3 text-xs font-semibold text-gray-600">
                {t("customers.table.pass")}
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-600">
                {t("customers.table.balance")}
              </th>
              <th scope="col" className="px-4 py-3 text-xs font-semibold text-gray-600">
                {t("customers.table.lastVisit")}
              </th>
              <th scope="col" className="px-4 py-3 text-xs font-semibold text-gray-600">
                {t("customers.table.joined")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : (
              customers.map((customer) => {
                const on = customer.id === selectedId;
                const segment = segmentForLastVisit(customer.last_visit_at, now);
                const name = customer.name || t("customers.unnamed");
                return (
                  <tr
                    key={customer.id}
                    className={`border-t border-gray-100 ${on ? "bg-indigo-50/60" : "bg-white hover:bg-gray-50"}`}
                  >
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        onClick={() => onSelect(customer)}
                        aria-pressed={on}
                        aria-label={t("customers.list.show", { name })}
                        className="flex min-w-0 items-center gap-3 text-left"
                      >
                        <Avatar name={customer.name || customer.email} className="h-9 w-9 text-[13px]" />
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate font-medium text-gray-900">{name}</span>
                          <span
                            className={`truncate text-xs ${customer.email ? "text-gray-600" : "text-orange-800"}`}
                          >
                            {customer.email || t("customers.list.noEmail")}
                          </span>
                        </span>
                      </button>
                    </td>
                    <td className="px-4 py-2.5">
                      <CardCell pass={customer.pass} />
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold whitespace-nowrap text-gray-900 tabular-nums">
                      {formatBalance(customer.point, balanceUnit(customer.pass?.type), {
                        t,
                        language,
                        currency: selectedStore?.currency,
                      })}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-700">
                      <span className="inline-flex items-center gap-2">
                        {showSegments && <SegmentDot segment={segment} />}
                        {customer.last_visit_at
                          ? formatRelative(customer.last_visit_at, language, now)
                          : t("customers.list.never")}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-600 tabular-nums">
                      {formatDate(customer.created_at, language) || "—"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {empty}

      {!loading && total > 0 && (
        <nav
          aria-label={t("customers.list.pagination")}
          className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3"
        >
          <span role="status" className="text-[13px] text-gray-600 tabular-nums">
            {filtered
              ? t("customers.list.summaryFiltered", summaryValues)
              : t("customers.list.summary", summaryValues)}
          </span>
          {totalPages > 1 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => onPage(current - 1)}
                disabled={current <= 1}
                className={pagerClass(current > 1)}
              >
                {t("pagination.previous")}
              </button>
              {pageWindow(current, totalPages).map((p) => {
                const isCurrent = p === current;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => onPage(p)}
                    aria-label={t("customers.list.page", { page: p })}
                    aria-current={isCurrent ? "page" : undefined}
                    className={`min-h-10 min-w-10 rounded-lg border px-2.5 text-[13px] tabular-nums ${
                      isCurrent
                        ? "border-indigo-600 bg-indigo-50 font-semibold text-indigo-800"
                        : "border-gray-300 bg-white font-medium text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    {formatNumber(p, language)}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => onPage(current + 1)}
                disabled={current >= totalPages}
                className={pagerClass(current < totalPages)}
              >
                {t("pagination.next")}
              </button>
            </div>
          )}
        </nav>
      )}
    </section>
  );
}
